import { Controller, useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  AdvertDestination,
  AdvertDestinationHelperLabel,
  AdvertDestinationLabel,
  Experience,
} from '@/types';
import { LabelField } from '@/components';
import {
  Stack,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from '@silverstein-properties/inspirelabs-ui';
import { AdvertDestinationSectionEditInput } from '@/classes/AdvertDestinationSectionEditInput';
import { FormHelperText } from '@mui/material';

const resolver = classValidatorResolver(AdvertDestinationSectionEditInput, {
  transformer: { enableImplicitConversion: true },
});

export type AdvertDestinationSectionEditPropsType = {
  experience: Experience;
  onFormSubmit: (data: AdvertDestinationSectionEditInput) => Promise<void>;
};

export const AdvertDestinationSectionEdit = ({
  experience,
  onFormSubmit,
}: AdvertDestinationSectionEditPropsType) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<AdvertDestinationSectionEditInput>({
    resolver,
    mode: 'onTouched',
    defaultValues: {
      advertDestination: experience.advertDestination,
    },
  });

  return (
    <Stack spacing={4}>
      <Typography variant="h5">Promotion Destination</Typography>
      <form id="advertDestinationForm" onSubmit={handleSubmit(onFormSubmit)}>
        <Stack spacing={4} sx={{ width: '100%' }}>
          <LabelField
            label="Where should the promotion lead?"
            value={
              <Box sx={{ display: 'inline-flex', mt: 2 }}>
                <Controller
                  control={control}
                  name="advertDestination"
                  render={({ field }) => (
                    <FormControl error={!!errors.advertDestination}>
                      <RadioGroup
                        {...field}
                        ref={undefined}
                        data-testid="advertDestinationEdit"
                      >
                        <FormControlLabel
                          value={AdvertDestination.EXTERNAL_LINK}
                          control={<Radio />}
                          sx={{ alignItems: 'flex-start', mb: 2 }}
                          label={
                            <>
                              <Typography mt={0.875}>
                                {
                                  AdvertDestinationLabel[
                                    AdvertDestination.EXTERNAL_LINK as keyof typeof AdvertDestinationLabel
                                  ]
                                }
                              </Typography>
                              <Typography
                                color="primary.medium"
                                variant="body2"
                              >
                                {
                                  AdvertDestinationHelperLabel[
                                    AdvertDestination.EXTERNAL_LINK as keyof typeof AdvertDestinationHelperLabel
                                  ]
                                }
                              </Typography>
                            </>
                          }
                        />
                        <FormControlLabel
                          value={AdvertDestination.DEEP_LINK}
                          control={<Radio />}
                          sx={{ alignItems: 'flex-start', mb: 2 }}
                          label={
                            <>
                              <Typography mt={0.875}>
                                {
                                  AdvertDestinationLabel[
                                    AdvertDestination.DEEP_LINK as keyof typeof AdvertDestinationLabel
                                  ]
                                }
                              </Typography>
                              <Typography
                                color="primary.medium"
                                variant="body2"
                              >
                                {
                                  AdvertDestinationHelperLabel[
                                    AdvertDestination.DEEP_LINK as keyof typeof AdvertDestinationHelperLabel
                                  ]
                                }
                              </Typography>
                            </>
                          }
                        />
                      </RadioGroup>
                      <FormHelperText sx={{ fontWeight: 300, ml: 0 }}>
                        {errors.advertDestination?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Box>
            }
          />
        </Stack>
      </form>
    </Stack>
  );
};
